import type { AdvisorMovement } from "@/types";
import { buildReceivedGoods } from "@/utils/accounting";
import { formatCOP, formatNumber } from "@/utils/format";

interface AdvisorReceivedGoodsProps {
  movements: AdvisorMovement[];
}

// Mercancía que le llegó al asesor (consignación y compras), agrupada por producto.
// Se actualiza cada vez que recibe mercancía, sea pagada o en consignación.
const AdvisorReceivedGoods = ({ movements }: AdvisorReceivedGoodsProps) => {
  const rows = buildReceivedGoods(movements);
  const totalUnits = rows.reduce((a, r) => a + r.totalUnits, 0);
  const totalValue = rows.reduce((a, r) => a + r.totalValue, 0);

  return (
    <div className="rounded-lg border border-secondary-500/25 bg-background-200/30 p-3.5">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-secondary-200">
          Mercancía recibida
        </h4>
        <span className="text-[10px] text-foreground-500">
          {formatNumber(totalUnits)} und · {formatCOP(totalValue)}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="text-[11px] text-foreground-500 py-2">
          Aún no se le ha entregado mercancía a este asesor.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-foreground-500 text-left">
                <th className="font-semibold py-1.5 pr-3">Producto</th>
                <th className="font-semibold py-1.5 px-2 text-right whitespace-nowrap">Consignado</th>
                <th className="font-semibold py-1.5 px-2 text-right whitespace-nowrap">Comprado</th>
                <th className="font-semibold py-1.5 pl-2 text-right whitespace-nowrap">Valor total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.productName} className="border-t border-background-300/40">
                  <td className="py-1.5 pr-3">
                    <span className="text-foreground-800 font-medium">{r.productName}</span>
                    {r.fromExcel && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-accent-500/20 text-accent-200">
                        <i className="ri-file-excel-2-line" /> Excel
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right text-secondary-200 whitespace-nowrap">
                    {r.consignedUnits > 0 ? (
                      <>
                        {formatNumber(r.consignedUnits)} und
                        <span className="block text-[10px] text-foreground-500">
                          {formatCOP(r.consignedValue)}
                        </span>
                      </>
                    ) : (
                      <span className="text-foreground-600">—</span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-right text-primary-200 whitespace-nowrap">
                    {r.purchasedUnits > 0 ? (
                      <>
                        {formatNumber(r.purchasedUnits)} und
                        <span className="block text-[10px] text-foreground-500">
                          {formatCOP(r.purchasedValue)}
                        </span>
                      </>
                    ) : (
                      <span className="text-foreground-600">—</span>
                    )}
                  </td>
                  <td className="py-1.5 pl-2 text-right font-bold text-foreground-950 whitespace-nowrap">
                    {formatCOP(r.totalValue)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[10px] text-foreground-500 mt-2">
        Consignación = mercancía del admin que el asesor paga al venderla. Comprado = unidades ya
        pagadas por el asesor. Los valores son a costo asesor.
      </p>
    </div>
  );
};

export default AdvisorReceivedGoods;